import React, {useState, useEffect, Fragment} from 'react';
import {Controller, get, useWatch} from 'react-hook-form';
import SubHeader from 'app/gakki/components/SubHeader';
import Select from 'components/Select';
import {transferDateObj, formatYYYYMMDD} from 'app/gakki/utils/time';
import {
  FormItem,
  Input,
  DatePicker,
  ErrorMessage,
  Switch,
} from 'components/form-items';
import {DateTime} from 'luxon';
import Block from './block';
import Show from './show';
import {Occupational_Injury_Insurance_EffectiveDate} from 'const';

const nationality = [
  {
    label: '本國人',
    value: 0,
  },
  {
    label: '外籍',
    value: 1,
  },
  {
    label: '外籍配偶',
    value: 2,
  },
  {
    label: '大陸配偶',
    value: 3,
  },
];

const yesorno = [
  {
    label: '是',
    value: true,
  },
  {
    label: '否',
    value: false,
  },
];
const OccupationalInjuryInsurance = (props: any) => {
  const {
    formProps,
    defaultValues,
    options,
    onBoardingDate,
    getInsuranceAmount,
    updateLocale,
    defaultEmployeeId,
    presetEffectiveDate,
    shouldPresetEffectiveDate,
  } = props;
  const {control, formState, errors, setValue, trigger} = formProps;

  const isUnable = !useWatch({
    control,
    name: `occupationalInjuryInsurance.enabled`,
    defaultValue: defaultValues.enabled,
  });
  const isDeduction = useWatch({
    control,
    name: `occupationalInjuryInsurance.isDeduction`,
    defaultValue: defaultValues.isDeduction,
  });
  const effectiveDate = useWatch({
    control,
    name: `occupationalInjuryInsurance.effectiveDate`,
    defaultValue: defaultValues.effectiveDate,
  });
  const insuranceIdentityId = useWatch({
    control,
    name: `occupationalInjuryInsurance.insuranceIdentityId`,
    defaultValue: defaultValues.insuranceIdentityId,
  });
  const employeeId = useWatch({
    control,
    name: `employee.id`,
    defaultValue: defaultEmployeeId,
  });
  const [insuranceAmount, setInsuranceAmount] = useState([]);
  const [isInsuranceAmountFetching, setIsInsuranceAmountFetching] =
    useState(false);
  const isError = (name: string) =>
    !isUnable && formState.isSubmitted && get(errors, name);
  const renderErrorMessage = (name: string) =>
    isError(name) && <ErrorMessage>{get(errors, name)?.message}</ErrorMessage>;

  useEffect(() => {
    if (!isUnable && formState.isSubmitted) {
      trigger();
    }
  }, [isUnable, formState.isSubmitted]);
  useEffect(() => {
    if (
      defaultValues.effectiveDate &&
      defaultValues.insuranceIdentityId &&
      employeeId
    ) {
      setIsInsuranceAmountFetching(true);
      getInsuranceAmount({
        amountLevelType: 4,
        insuranceAmountLevelId: undefined,
        effectiveDate: defaultValues.effectiveDate,
        insuranceIdentityId: defaultValues.insuranceIdentityId,
      })
        .then((res: any) => {
          setInsuranceAmount(res.data);
        })
        .finally(() => {
          setIsInsuranceAmountFetching(false);
        });
    }
  }, [defaultValues.effectiveDate, defaultValues.insuranceIdentityId, employeeId]);

  const updateInsuranceAmount = ({
    effectiveDateInput,
    insuranceIdentityIdInput,
  }: {
    effectiveDateInput?: string;
    insuranceIdentityIdInput?: string;
  }) => {
    if (
      (effectiveDateInput || effectiveDate) &&
      (insuranceIdentityIdInput || insuranceIdentityId)
    ) {
      setIsInsuranceAmountFetching(true);
      setInsuranceAmount([]);
      getInsuranceAmount({
        amountLevelType: 4,
        insuranceAmountLevelId: undefined,
        effectiveDate: effectiveDateInput || effectiveDate,
        insuranceIdentityId: insuranceIdentityIdInput || insuranceIdentityId,
      })
        .then((res: any) => {
          setInsuranceAmount(res.data);
        })
        .finally(() => {
          setIsInsuranceAmountFetching(false);
        });
    }
  };
  const minDate =
    onBoardingDate &&
    new Date(onBoardingDate) >
      new Date(Occupational_Injury_Insurance_EffectiveDate)
      ? onBoardingDate
      : Occupational_Injury_Insurance_EffectiveDate;

  return (
    <div data-test-id={'injury-insurance'}>
      <SubHeader>
        <span style={{paddingRight: '15px'}}>職保</span>
        <Controller
          render={({onChange, value}) => (
            <div data-test-id={'enable-injury-insurance'} className="flex items-center">
              <Switch
                checked={value}
                onChange={(e: any) => {
                  onChange(e);
                  updateLocale();
                }}
              />
            </div>
          )}
          name={`occupationalInjuryInsurance.enabled`}
          control={control}
          defaultValue={defaultValues.enabled}
        />
      </SubHeader>
      <Block>
        <Show isShow>
          <FormItem
            label="生效日期"
            itemName="occupationalInjuryInsurance_effectiveDate"
            required
            width="160px"
            data-test-id={'effective-date'}
          >
            <Controller
              render={({onChange, value}) => (
                <Fragment>
                  <DatePicker
                    dateFormat="yyyy/MM/dd"
                    minDate={transferDateObj(minDate)}
                    selected={transferDateObj(value)}
                    disabled={formState.isSubmitting || isUnable}
                    onChange={(date: Date) => {
                      const dateString = formatYYYYMMDD(date);
                      onChange(dateString);
                      setValue(
                        `occupationalInjuryInsurance.insuranceAmountLevelId`,
                        null,
                      );
                      updateInsuranceAmount({effectiveDateInput: dateString});
                      updateLocale();
                    }}
                    onBlur={() => {
                      if (value && shouldPresetEffectiveDate === 'true') {
                        presetEffectiveDate(value)
                      }
                    }}
                    onCalendarClose={() => {
                      if (value && shouldPresetEffectiveDate === 'true') {
                        presetEffectiveDate(value)
                      }
                    }}
                    error={isError(`occupationalInjuryInsurance.effectiveDate`)}
                  />
                  {renderErrorMessage(`occupationalInjuryInsurance.effectiveDate`)}
                </Fragment>
              )}
              name={`occupationalInjuryInsurance.effectiveDate`}
              control={control}
              rules={{
                required: !isUnable && '生效日期為必填',
                validate: {
                  isAfterOnBoardingDate: v => {
                    let minDateObj = transferDateObj(minDate);
                    let effectiveDateObj = transferDateObj(v);
                    if (!isUnable && minDateObj && effectiveDateObj) {
                      return (
                        DateTime.fromJSDate(minDateObj) <=
                          DateTime.fromJSDate(effectiveDateObj) ||
                        `不可早於 ${minDate}`
                      );
                    }
                    return true;
                  },
                },
              }}
              defaultValue={defaultValues.effectiveDate}
            />
          </FormItem>
          <FormItem
            label="籍別"
            itemName="occupationalInjuryInsurance_nationality"
            required
            width="120px"
            data-test-id={'nationality'}
          >
            <Controller
              render={({onChange, value}) => (
                <Select
                  value={nationality.find(o => o.value === value)}
                  options={nationality}
                  onChange={(o: {value: string}) => {
                    onChange(o.value);
                    updateLocale();
                  }}
                  isDisabled={formState.isSubmitting || isUnable}
                />
              )}
              name={`occupationalInjuryInsurance.nationality`}
              control={control}
              defaultValue={defaultValues.nationality}
            />
          </FormItem>
          <FormItem
            label="投保單位"
            itemName="occupationalInjuryInsurance_insuranceApplicantUnitId"
            required
            width="200px"
            data-test-id={'applicant-unit-id'}
          >
            <Controller
              render={({onChange, value}) => (
                <Select
                  value={
                    options.insuranceUnits.find(
                      (o: any) => o.insuranceApplicantUnitId === value,
                    ) || null
                  }
                  options={options.insuranceUnits}
                  onChange={(o: any) => {
                    onChange(o.insuranceApplicantUnitId);
                    updateLocale();
                  }}
                  isDisabled={formState.isSubmitting || isUnable}
                  getOptionLabel={(o: any) =>
                    `${o.insuranceNo} ${o.insuranceApplicantUnitName}`
                  }
                  getOptionValue={(o: any) => o.insuranceApplicantUnitId}
                  error={isError(
                    `occupationalInjuryInsurance.insuranceApplicantUnitId`,
                  )}
                />
              )}
              name={`occupationalInjuryInsurance.insuranceApplicantUnitId`}
              control={control}
              rules={{required: !isUnable && '投保單位為必填'}}
              defaultValue={defaultValues.insuranceApplicantUnitId}
            />
          </FormItem>
          <FormItem
            label="投保身分"
            itemName="occupationalInjuryInsurance_insuranceIdentityId"
            required
            width="200px"
            data-test-id={'insurance-identity-id'}
          >
            <Controller
              render={({onChange, value}) => (
                <Select
                  value={
                    options.insuranceIdentity.find(
                      (o: any) => o.value === value,
                    ) || null
                  }
                  options={options.insuranceIdentity}
                  onChange={(o: {value: string}) => {
                    onChange(o.value);
                    setValue(
                      `occupationalInjuryInsurance.insuranceAmountLevelId`,
                      null,
                    );
                    updateInsuranceAmount({insuranceIdentityIdInput: o.value});
                    updateLocale();
                  }}
                  isDisabled={formState.isSubmitting || isUnable}
                  getOptionLabel={(o: any) => o.item}
                  error={isError(
                    `occupationalInjuryInsurance.insuranceIdentityId`,
                  )}
                />
              )}
              name={`occupationalInjuryInsurance.insuranceIdentityId`}
              control={control}
              rules={{required: !isUnable && '投保身分為必填'}}
              defaultValue={defaultValues.insuranceIdentityId}
            />
          </FormItem>
          <FormItem
            label="投保金額"
            itemName="occupationalInjuryInsurance_insuranceAmountLevelId"
            required
            width="135px"
            data-test-id={'insurance-amount-level-id'}
          >
            <Controller
              render={({onChange, value}) => (
                <Select
                  isLoading={isInsuranceAmountFetching}
                  loadingMessage={() => '載入中...'}
                  value={
                    insuranceAmount.find((o: any) => o.value === value) || ''
                  }
                  options={insuranceAmount}
                  onChange={(o: {value: string}) => {
                    onChange(o.value);
                    updateLocale();
                  }}
                  onMenuOpen={() => {
                    if (insuranceAmount.length === 0) {
                      updateInsuranceAmount({});
                    }
                  }}
                  isDisabled={formState.isSubmitting || isUnable}
                  getOptionLabel={(o: any) => o.item}
                  error={isError(
                    `occupationalInjuryInsurance.insuranceAmountLevelId`,
                  )}
                />
              )}
              name={`occupationalInjuryInsurance.insuranceAmountLevelId`}
              control={control}
              rules={{required: !isUnable && '投保金額為必填'}}
              defaultValue={defaultValues.insuranceAmountLevelId}
            />
          </FormItem>
          <FormItem
            label="減免身分"
            itemName="occupationalInjuryInsurance_isDeduction"
            required
            width="80px"
            data-test-id={'is-deduction'}
          >
            <Controller
              render={({onChange, value}) => (
                <Select
                  value={yesorno.find(o => o.value === value)}
                  options={yesorno}
                  onChange={(o: {value: string}) => {
                    onChange(o.value);
                    updateLocale();
                  }}
                  isDisabled={formState.isSubmitting || isUnable}
                />
              )}
              name={`occupationalInjuryInsurance.isDeduction`}
              control={control}
              defaultValue={defaultValues.isDeduction}
            />
          </FormItem>
          <FormItem
            label="補助費率"
            itemName="occupationalInjuryInsurance_allowanceRate"
            required
            width="100px"
            hidden={!isDeduction}
            data-test-id={'allowance-rate'}
          >
            <Controller
              render={({onChange, value}) => (
                <Input
                  value={value}
                  disabled={formState.isSubmitting || isUnable}
                  onChange={e => {
                    const {value} = e.target;
                    if (
                      !value ||
                      /^(\d+(\.\d{0,2})?|\.?\d{1,2})$/.test(value)
                    ) {
                      onChange(value);
                      updateLocale();
                    }
                  }}
                  error={
                    isDeduction &&
                    isError(`occupationalInjuryInsurance.allowanceRate`)
                  }
                />
              )}
              name={`occupationalInjuryInsurance.allowanceRate`}
              control={control}
              rules={{
                required: isDeduction && !isUnable && '補助費率為必填',
                min: {
                  value: 0,
                  message: '最小值為0',
                },
                max: {
                  value: 100,
                  message: '最大值為100',
                },
              }}
              defaultValue={defaultValues.allowanceRate}
            />
            {renderErrorMessage(`occupationalInjuryInsurance.allowanceRate`)}
          </FormItem>
          <FormItem
            label="減免備註"
            itemName="occupationalInjuryInsurance_allowanceRemark"
            width="135px"
            hidden={!isDeduction}
            data-test-id={'allowance-remark'}
          >
            <Controller
              render={({onChange, value}) => (
                <Input
                  value={value}
                  disabled={formState.isSubmitting || isUnable}
                  onChange={(e: any) => {
                    onChange(e);
                    updateLocale();
                  }}
                  maxLength={250}
                />
              )}
              name={`occupationalInjuryInsurance.allowanceRemark`}
              control={control}
              defaultValue={defaultValues.allowanceRemark}
            />
          </FormItem>
        </Show>
      </Block>
    </div>
  );
};

export default OccupationalInjuryInsurance;
